import React from 'react';
import { canEnableDomainCapability, countEnabledOrigins, getOriginLimit } from '../../utils/siteDomainConfiguration';

const controls = [
  ['consumerExperience', 'Extension rendering'],
  ['heat_map', 'Basic heatmap capture'],
  ['customer_analytics', 'Advanced customer analytics'],
  ['session_replay', 'Session replay recording']
];

export default function DomainCapabilityControls({
  organizationId, configurations, entitlements, loading, disabled, isSiteStorageReady = true, replayPolicy, onChange, onProvisionStorage
}) {
  const limit = getOriginLimit(entitlements);

  return (
    <section aria-label="Domain capabilities" className="mt-6 rounded-lg border border-border bg-card p-5 text-card-foreground shadow-sm space-y-4">
      <h4 className="text-base font-semibold tracking-tight text-foreground">Domain rendering and capture consent</h4>
      <p role="status" className="rounded-md border border-blue-200 bg-blue-50/70 p-3 text-xs text-blue-900 leading-relaxed">
        {loading ? 'Loading organization entitlements — new enablement is disabled.' :
          !entitlements ? 'Entitlements unavailable — new enablement is disabled.' :
            `Plan origin limit: ${limit === -1 ? 'unlimited' : limit ?? 'unavailable'}. Authoritative usage and admission are shown in saved domain state.`}
        {' '}Rendering or basic capture counts once per distinct organization domain, even with both on.
        {' '}This draft requests {countEnabledOrigins(configurations)} distinct domains.
      </p>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Enabling capture records your organization’s consent to collect permitted interaction data.
        You are responsible for required end-user notice and consent. Local privacy preferences still
        apply. Turning capture off stops future collection; it does not delete retained data.
        Rendering only displays guides and does not authorize capture. All new choices default off.
      </p>
      {configurations.map(configuration => (
        <fieldset key={configuration.domain} disabled={disabled} className="rounded-md border border-border bg-background/50 p-4 space-y-3 disabled:opacity-60">
          <legend className="px-2 font-mono text-xs font-semibold text-foreground bg-background rounded border border-border">{configuration.domain}</legend>
          {configuration.domain.includes('*') && <p className="rounded border border-amber-200 bg-amber-50/80 p-2.5 text-xs text-amber-900 leading-relaxed">Wildcard domains remain available for standalone SDK transport.
            Extension rendering and capture require a concrete domain. Register a concrete domain to enable these capabilities; the legacy wildcard is not removed.</p>}
          {controls.map(([flag, label]) => {
            const allowed = !loading && canEnableDomainCapability(flag, configuration, entitlements, isSiteStorageReady);
            const isReplay = flag === 'session_replay';
            const showBlockedReason = isReplay && replayPolicy?.sessionReplayEffective === false && replayPolicy?.sessionReplayBlockedReason;

            return (
              <div key={flag} className="py-1">
                <label className="flex items-start gap-2.5 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={Boolean(configuration[flag])}
                    disabled={!configuration[flag] && !allowed}
                    className="mt-0.5 h-4 w-4 rounded border-border accent-primary cursor-pointer disabled:cursor-not-allowed"
                    onChange={event => {
                      if (!disabled && (!event.target.checked || allowed)) {
                        onChange(configuration.domain, flag, event.target.checked);
                      }
                    }}
                  />
                  <span className="flex-1 text-foreground">
                    {label}
                  </span>
                </label>
                {isReplay && configuration[flag] && (
                  <small className="block ml-6 text-blue-700 font-medium mt-0.5" data-testid="replay-retention-notice">
                    Retention policy: 14 UTC days maximum (server enforced).
                  </small>
                )}
                {!configuration[flag] && !allowed && !configuration.domain.includes('*') && (
                  isReplay ? (
                    (!configuration.customer_analytics || !configuration.heat_map) ? (
                      <small className="block ml-6 text-muted-foreground italic mt-0.5">
                        Requires basic heatmap and customer analytics to be enabled.
                      </small>
                    ) : !isSiteStorageReady ? (
                      <small className="block ml-6 text-muted-foreground italic mt-0.5">
                        Requires site storage container to be ready.
                        {onProvisionStorage && (
                          <button
                            type="button"
                            onClick={() => onProvisionStorage()}
                            className="ml-2 font-medium text-blue-600 underline not-italic hover:text-blue-800 cursor-pointer"
                          >
                            Provision now
                          </button>
                        )}
                      </small>
                    ) : null
                  ) : flag === 'customer_analytics' && !configuration.heat_map ? (
                    <small className="block ml-6 text-muted-foreground italic mt-0.5">
                      Requires basic heatmap capture to be enabled.
                    </small>
                  ) : null
                )}
                {showBlockedReason && (
                  <div
                    className="mt-1 ml-6 rounded bg-amber-50 border border-amber-200 px-2 py-1 text-xs text-amber-800 flex items-center justify-between"
                    role="alert"
                    data-testid="replay-blocked-reason"
                  >
                    <span>
                      Replay blocked: {
                        replayPolicy.sessionReplayBlockedReason === 'requires_heatmap_and_customer_analytics'
                          ? 'Requires basic heatmap and customer analytics to be enabled.'
                          : replayPolicy.sessionReplayBlockedReason === 'storage_not_ready'
                          ? 'Site storage container is not ready.'
                          : replayPolicy.sessionReplayBlockedReason
                      }
                    </span>
                    {replayPolicy.sessionReplayBlockedReason === 'storage_not_ready' && onProvisionStorage && (
                      <button
                        type="button"
                        onClick={() => onProvisionStorage()}
                        className="ml-2 font-medium text-amber-900 underline hover:text-amber-950 cursor-pointer"
                      >
                        Provision now
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </fieldset>
      ))}
    </section>
  );
}
